import {Component, OnInit, TemplateRef} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {AngularEditorConfig} from '@kolkov/angular-editor';
import {LocalStorageService} from 'src/app/local-storage.service';
import {DashboardService} from '../dashboard.service';
import {ToastrService} from 'ngx-toastr';
import {ImageCroppedEvent} from 'ngx-image-cropper';
import {Entity} from '../../core/models/entities/entity';
import {EDITOR_CONFIG} from '../../constants';
import {UtilsService} from '../../core/services/utils/utils.service';
import {ApiService} from '../../core/http/api.service';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import { HttpClient, HttpEventType } from '@angular/common/http';
import {InfoParams} from '../new-entity/new-entity.component';
import {createCroppedImageFile, getUploadProgress, isImageFile} from '../../core/services/utils/image-upload.utils';

@Component({
    selector: 'app-edit-entity',
    templateUrl: './edit-entity.component.html',
    styleUrls: ['./edit-entity.component.css'],
    standalone: false
})
export class EditEntityComponent implements OnInit {

  // IMAGE UPLOAD
  modalRef: BsModalRef;
  croppedFile: any;
  aspectRatio = 1;
  uploading = false;
  progress = 0;

  informativeParameters: InfoParams[] = [];
  entityId = '';
  entity: Entity;
  entities: Array<Entity>;
  relatedEntities: Array<any> = [];
  childEntities: Array<any> = [];
  editorConfig: AngularEditorConfig = EDITOR_CONFIG;
  selectedRelatedEntities = null;
  selectedChildEntities = null;
  searchKeywords: any = {};
  infoParamsOfCategory: any[] = [];
  // Cropper Items
  imageChangedEvent: any = '';
  croppedImage: any = '';

  isSubmitting = false;

  constructor(
    private localStorage: LocalStorageService,
    private route: ActivatedRoute,
    private dashApi: DashboardService,
    private api: ApiService,
    private toastr: ToastrService,
    private modalService: BsModalService,
    private httpClient: HttpClient,
    public utils: UtilsService) {
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => this.entityId = params.id);
    this.getCurrentEntityData();
    this.getAllEntities();
  }

  getCurrentEntityData(): void {
    this.dashApi.getSingleEntity(this.entityId).subscribe((data) => {
      this.entity = data;
      this.getInfoParamsByCategory(data.category.id);
      this.getRelatedEntities();
      this.getChildEntities();
      if (!this.entity.allowedUserActions) {
        this.entity['allowedUserActions'] = {bhakt: true, unbhakt: true, rating: true, story: true, hypocrisy: true};
      }
    }, err => this.utils.resErr(err));
  }

  getInfoParamsByCategory(id): void {
    this.dashApi.getInfoParamsByCategory(id).subscribe((data) => {
      this.infoParamsOfCategory = data;
      this.getLookupEntities(data);
    });
  }

  getRelatedEntities(): void {
    this.api.getRelatedEntities(this.entity.id).subscribe(res => {
      this.relatedEntities = Array.isArray(res?.response) ? res.response : [];
    }, err => this.utils.resErr(err));
  }

  getChildEntities(): void {
    this.api.getChildEntities(this.entity.id).subscribe(res => {
      this.childEntities = (res.response || []).map(item => {
        return {...item.childEntity || {}};
      });
    }, err => this.utils.resErr(err));
  }

  updateEntity(): void {
    if (this.isSubmitting || !this.entity) {
      return;
    }
    this.isSubmitting = true;
    // @ts-ignore
    this.entity.in_person = (this.entity.in_person === 'true'  || this.entity.in_person ===  true);
    const finalizeUpdate = () => {
      try {
        const informativeParameters = Array.isArray(this.entity?.infoData?.informativeParameters)
          ? this.entity.infoData.informativeParameters
          : [];

        const temp: any = {
          ...this.entity,
          category: this.entity?.category?.id || this.entity?.category?._id || this.entity?.category,
          related_entity: (this.relatedEntities || []).map(e => e.id),
          child_entity: (this.childEntities || []).map(e => e.id),
          subjectiveData: this.entity?.subjectiveData?.value?.map(sub => sub?.value) || [],
          infoData: informativeParameters.map((info: any) => {
            const formatted = {...info, field: info?.id || info?.field};
            if (formatted.fieldType !== 'lookUp') {
              delete formatted.lookUpRecordId;
            }
            return formatted;
          })
        };

        this.dashApi.updateEntity(temp).subscribe(() => {
          this.toastr.success('Entity Updated');
          this.utils.goBack();
          this.isSubmitting = false;
        }, err => {
          this.utils.resErr(err);
          this.isSubmitting = false;
        });
      } catch (error) {
        this.utils.resErr(error);
        this.isSubmitting = false;
      }
    };

    if (this.croppedImage) {
      this.dashApi.uploadImage({file: this.croppedImage}).subscribe((res2) => {
        this.entity.image = res2.response.url;
        finalizeUpdate();
      }, err => {
        this.utils.resErr(err);
        this.isSubmitting = false;
      });
      return;
    }

    finalizeUpdate();
  }

  handleClickUpload(): void {
    const ele = document.getElementById('dp_entity');
    if (!!ele) {
      ele.click();
    }
  }

  // IMAGE CROPPER
  fileChangeEvent(event: any, template: TemplateRef<any>): void {
    const file = event?.target?.files?.[0];
    if (!file) {
      return;
    }
    if (!isImageFile(file)) {
      this.utils.toastError('Please select an image file only');
      event.target.value = '';
      return;
    }
    this.modalRef = this.modalService.show(template);
    this.aspectRatio = 1;
    this.croppedFile = null;
    this.imageChangedEvent = event;
  }

  uploadImage(): any {
    if (!this.croppedFile) {
      this.utils.toastError('Please crop the selected image');
      return;
    }
    this.uploading = true;
    const formData = new FormData();
    formData.append('file', this.croppedFile, this.croppedFile?.name || `upload-${Date.now()}.png`);
    const upload = this.httpClient.post<any>('file-upload', formData, {
      reportProgress: true,
      observe: 'events'
    });
    upload.subscribe(
      (res: any) => {
        if (res.type === HttpEventType.UploadProgress) {
          this.progress = getUploadProgress(res.loaded, res.total);
        } else if (res.type === HttpEventType.Response) {
          this.uploading = false;
          this.entity.image = res.body.response.url;
          this.progress = 0;
          this.closeCropperModal();
        }
      },
      (err) => {
        this.uploading = false;
        this.utils.resErr(err);
      }
    );
  }

  imageCropped(event: ImageCroppedEvent): void {
    this.croppedFile = createCroppedImageFile(event);
  }

  loadImageFailed(): void {
    this.utils.toastError('Image Load failed!');
  }

  closeCropperModal(): void {
    if (this.uploading) {
      this.utils.toastError('Please wait for upload to complete');
      return;
    }
    if (this.modalRef) {
      this.modalRef.hide();
    }
    if (this.imageChangedEvent?.target) {
      this.imageChangedEvent.target.value = '';
    }
    this.imageChangedEvent = null;
    this.croppedFile = null;
    this.uploading = false;
    this.progress = 0;
  }

  // Related Entities
  getAllEntities(): void {
    this.api.getAllEntities({category: null, sort: 'DESC'}).subscribe(res => {
      this.entities = res?.response?.entities || [];
    });
  }

  toggleEntity(eId, isChildEntities = false): void {
    if (!eId || !Array.isArray(this.entities)) {
      return;
    }
    const temp = this.entities.find(e => e.id === eId);
    if (!temp) {
      return;
    }
    if (isChildEntities) {
      if (this.childEntities.find(p => p.id === temp.id)) {
        this.toastr.error('This Entity is already selected');
      } else {
        this.childEntities.push({id: temp.id, name: temp.firstName + ' ' + temp.lastName});
      }
    } else {
      if (this.relatedEntities.find(p => p.id === temp.id)) {
        this.toastr.error('This Entity is already selected');
      } else {
        this.relatedEntities.push({id: temp.id, name: temp.firstName + ' ' + temp.lastName});
      }
    }

    setTimeout(() => {
      this.selectedRelatedEntities = null;
      this.selectedChildEntities = null;
    }, 250);
  }

  removeEntity = (id, isChildEntities = false) => {
    if (isChildEntities) {
      this.childEntities.splice(this.childEntities.findIndex(x => x.id === id), 1);
    } else {
      this.relatedEntities.splice(this.relatedEntities.findIndex(x => x.id === id), 1);
    }
  };

  onAllowedUserActionChange(key: string, value: boolean): void {
    if (!this.entity) {
      return;
    }
    this.entity.allowedUserActions = this.entity.allowedUserActions || {
      bhakt: true,
      unbhakt: true,
      rating: true,
      story: true,
      hypocrisy: true
    };
    this.entity.allowedUserActions[key] = value;
  }

  handleSelectInfo(event: any, index: number): void {
    this.entity.infoData.informativeParameters[index].metadata = event;
    this.entity.infoData.informativeParameters[index].value = event.id;
  }

  getLookupEntities(infoParamsOfCategory: any = []): void {
    if (!!this.entity.infoData.informativeParameters?.length) {
      this.entity.infoData.informativeParameters.forEach(info => {
        const findInfoParams = infoParamsOfCategory.find(val => val.fieldName === info.fieldName);
        if (info.fieldType === 'lookUp' && findInfoParams) {
          this.dashApi.searchEntities({ category: findInfoParams.lookUpRecordId }).subscribe(
            res => {
              info.lookupEntities = res.response.entities;
              info.metadata = info.metadata || { name: '' };
            },
            err => this.utils.resErr(err)
          );
        }
      });
      const newInfoParamsOfCategory = infoParamsOfCategory.filter(item =>
        this.entity.infoData.informativeParameters?.some(val => val.fieldName !== item.fieldName));
      if (newInfoParamsOfCategory.length) {
        newInfoParamsOfCategory.map(async info => {
          if (info.fieldType === 'lookUp') {
            await this.dashApi.searchEntities({category: info.lookUpRecordId}).subscribe(
              res => {
                info.lookupEntities = res.response.entities;
                info.metadata = info.metadata || {name: ''};
              },
              err => this.utils.resErr(err)
            );
            return {...info};
          } else {
            return {...info, metadata: {}, lookupEntities: []};
          }
        });

      }
    } else {
      this.onOptionsSelected(this.entity.category?.id);
    }
  }

  onOptionsSelected(value: string): void {
    this.dashApi.getInfoParamsByCategory(value).subscribe((data) => {
      const searchPromises = data.map(info => {
        if (info.fieldType === 'lookUp') {
          return new Promise((resolve, reject) => {
            this.dashApi.searchEntities({category: info.lookUpRecordId}).subscribe(res => {
              resolve(res.response.entities);
            }, err => {
              this.utils.resErr(err);
              reject(err);
            });
          });
        } else {
          return Promise.resolve([]);
        }
      });

      Promise.all(searchPromises).then(lookUpEntitiesArray => {
        this.entity.infoData.informativeParameters = data.map((info, index) => ({
          ...info,
          lookupEntities: info.fieldType === 'lookUp' ? lookUpEntitiesArray[index] : [],
          metadata: {}
        }));
      });
    });
  }

  filterEntities(fieldName, keyword = ''): any {
    const findInfoParams = this.infoParamsOfCategory.find(val => val.fieldName === fieldName);
    if (!!findInfoParams && !!this.entities?.length) {
      const categoryId = findInfoParams?.lookUpRecordId;
      return this.entities.filter(val => val.category?.id === categoryId);
    }
    return this.entities || [];
  }

  filterRelatedEntities(isChildEntities = false): any {
    const entities = Array.isArray(this.entities) ? this.entities : [];
    const related = Array.isArray(this.relatedEntities) ? this.relatedEntities : [];
    const child = Array.isArray(this.childEntities) ? this.childEntities : [];
    const currentEntityId = this.entity?.id;

    if (isChildEntities) {
      return entities.filter(entity => entity.id !== currentEntityId && !child.some(val => val.id === entity.id));
    } else {
      return entities.filter(entity => entity.id !== currentEntityId && !related.some(val => val.id === entity.id));
    }
  }
}
